import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { PurchaseRequest } from "src/api/generated/erp/db/types/tables/purchaseRequest";
import i18n from "i18next";
import { FormCommonProps } from "src/components/common/forms/types";
import { AavoForm } from "src/components/common/forms/AavoForm.tsx";
import { FormDateField } from "src/components/common/forms/fields/FormDateField";
import {
	PurchaseRequestFormApi,
	PurchaseRequestFormApi_InitialData,
} from "src/api/generated/erp/purchase/purchaseRequest/purchaseRequestFormApi";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField";
import { FormTextField } from "src/components/common/forms/fields/FormTextField";
import { useForm } from "react-hook-form";
import { requiredNonNegativeFloatRule, requireRule } from "src/components/common/forms/validation";

export interface PurchaseRequestFormProps extends FormCommonProps<number> {
	purchaseRequestId: number;
}

export const PurchaseRequestForm = ({ purchaseRequestId, ...other }: PurchaseRequestFormProps) => {
	return (
		<AsyncFetchRender
			fetch={() => PurchaseRequestFormApi.getInitialData({ purchaseRequestId })}
			content={(initialData) => (
				<PurchaseRequestFormContent initialData={initialData} {...other} />
			)}
		/>
	);
};

interface PurchaseRequestFormContentProps extends FormCommonProps<number> {
	initialData: PurchaseRequestFormApi_InitialData;
}

const PurchaseRequestFormContent = ({
	initialData: { purchaseRequest, supplierPartIdOptions },
	onFormEdited,
	...other
}: PurchaseRequestFormContentProps) => {
	const {
		control,
		handleSubmit,
		formState: { isDirty },
		watch,
	} = useForm<PurchaseRequest>({
		mode: "all",
		defaultValues: purchaseRequest,
	});

	if (onFormEdited) watch(onFormEdited);

	return (
		<AavoForm handleSubmit={handleSubmit} isDirty={isDirty} submit={submitForm} {...other}>
			<FormDateField
				control={control}
				name={"demandDate"}
				label={i18n.t("demand_date")}
				rules={requireRule()}
			/>
			<FormSelectField
				control={control}
				name={"supplierPartId"}
				label={i18n.t("supplier")}
				disableClearable
				options={supplierPartIdOptions.map((option) => ({
					key: option.supplierPartId,
					label: option.supplierName,
				}))}
				rules={requireRule()}
			/>
			<FormTextField
				control={control}
				name={"quantity"}
				label={i18n.t("quantity")}
				type={"number"}
				rules={requiredNonNegativeFloatRule()}
			/>
		</AavoForm>
	);
};

const submitForm = async (purchaseRequest: PurchaseRequest): Promise<number> => {
	const result = await PurchaseRequestFormApi.update({ purchaseRequest });
	return result.purchaseRequestId;
};
